import { type ReactNode } from 'react';
import { Link, Navigate, Route, Routes } from 'react-router-dom';
import { useAuth, type Persona } from './lib/auth';
import { Login } from './pages/Login';
import { SignInComplete } from './pages/SignInComplete';
import { LawSearch } from './pages/law/LawSearch';
import { LawDetail } from './pages/law/LawDetail';
import { CaseRegister } from './pages/judiciary/CaseRegister';
import { CaseDetail } from './pages/judiciary/CaseDetail';
import { CauseLists } from './pages/judiciary/CauseLists';
import { Gazette } from './pages/ag/Gazette';
import { GazetteIssueDetail } from './pages/ag/GazetteIssueDetail';
import { LegalNotices } from './pages/ag/LegalNotices';
import { Drafting } from './pages/ag/Drafting';
import { DraftingDetail } from './pages/ag/DraftingDetail';
import { LegalOpinions } from './pages/ag/LegalOpinions';
import { LegalOpinionDetail } from './pages/ag/LegalOpinionDetail';
import { Litigation } from './pages/ag/Litigation';
import { LitigationDetail } from './pages/ag/LitigationDetail';
import { Shell, type NavGroup } from './ui/Shell';

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Law of Seychelles',
    items: [{ to: '/law', label: 'Legislation Search', icon: '⚖' }],
  },
  {
    label: 'Judiciary',
    items: [
      { to: '/judiciary/cases', label: 'Case Register', icon: '📁' },
      { to: '/judiciary/cause-lists', label: 'Cause Lists', icon: '📅' },
    ],
  },
  {
    label: "Attorney General's Chambers",
    items: [
      { to: '/ag/gazette', label: 'Gazette', icon: '📰' },
      { to: '/ag/legal-notices', label: 'Legal Notices', icon: '📝' },
      { to: '/ag/drafting', label: 'Drafting Projects', icon: '🖋' },
      { to: '/ag/legal-opinions', label: 'Legal Opinions', icon: '⚖' },
      { to: '/ag/litigation', label: 'Litigation Watch-list', icon: '🏛' },
    ],
  },
];

const JUDICIARY_ROLES = ['COURT_CLERK', 'REGISTRAR_COURT', 'JUDGE', 'TRIBUNAL_MEMBER'];

/** Where to land a freshly-signed-in persona — their primary workspace, not always the same page. */
function landingFor(persona: Persona): string {
  const r = persona.roles;
  if (r.some((role) => JUDICIARY_ROLES.includes(role))) return '/judiciary/cases';
  if (r.includes('GAZETTE_EDITOR')) return '/ag/gazette';
  if (r.includes('LAW_REVISION_COMMISSIONER')) return '/law';
  if (r.includes('LEGISLATIVE_DRAFTER')) return '/ag/drafting';
  return '/law';
}

/** Minimal public-facing chrome for the unauthenticated Law of Seychelles pages — no sidebar, no persona. */
function PublicLawChrome({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-200 bg-white px-6 py-3.5">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <Link to="/law" className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-600 text-xs font-black text-white">SC</span>
            <span className="text-sm font-bold text-slate-900">One Gov Justice &amp; Legal Affairs — Law of Seychelles</span>
          </Link>
          <Link to="/" className="text-xs font-semibold text-brand-700 hover:underline">
            Staff sign-in →
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-6 py-7">{children}</main>
    </div>
  );
}

export function App() {
  const { persona } = useAuth();

  if (!persona) {
    return (
      <Routes>
        <Route path="/signin/complete" element={<SignInComplete />} />
        {/* SeyLII-grade public law portal: genuinely no auth required
            (m38-attorney-general/src/routes/{legislation,provisions,citations}.ts),
            so it stays reachable even before staff sign in. */}
        <Route
          path="/law"
          element={
            <PublicLawChrome>
              <LawSearch />
            </PublicLawChrome>
          }
        />
        <Route
          path="/law/:id"
          element={
            <PublicLawChrome>
              <LawDetail />
            </PublicLawChrome>
          }
        />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  const landing = landingFor(persona);

  return (
    <Routes>
      <Route element={<Shell title="Justice & Legal Affairs" groups={NAV_GROUPS} />}>
        <Route index element={<Navigate to={landing} replace />} />

        <Route path="law" element={<LawSearch />} />
        <Route path="law/:id" element={<LawDetail />} />

        <Route path="judiciary/cases" element={<CaseRegister />} />
        <Route path="judiciary/cases/:id" element={<CaseDetail />} />
        <Route path="judiciary/cause-lists" element={<CauseLists />} />

        <Route path="ag/gazette" element={<Gazette />} />
        <Route path="ag/gazette/:id" element={<GazetteIssueDetail />} />
        <Route path="ag/legal-notices" element={<LegalNotices />} />
        <Route path="ag/drafting" element={<Drafting />} />
        <Route path="ag/drafting/:id" element={<DraftingDetail />} />
        <Route path="ag/legal-opinions" element={<LegalOpinions />} />
        <Route path="ag/legal-opinions/:id" element={<LegalOpinionDetail />} />
        <Route path="ag/litigation" element={<Litigation />} />
        <Route path="ag/litigation/:id" element={<LitigationDetail />} />
      </Route>

      <Route path="*" element={<Navigate to={landing} replace />} />
    </Routes>
  );
}
